import { FlaskConical, GitBranch, Clock, CheckCircle2, Lock, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import type { WidgetAction } from "@/lib/mechanics/types";
import { useLocale } from "@/hooks/useLocale";

interface ResearchItem {
  id: string;
  name: string;
  category?: string;
  tier?: number;
  cost?: number;
  turns_required?: number;
  turns_remaining?: number;
  effects?: string;
  completed_turn?: number;
}

interface ResearchPanelProps {
  completed?: ResearchItem[];
  inProgress?: ResearchItem[];
  queue?: ResearchItem[];
  researchPoints?: number;
  pointsPerTurn?: number;
  currency?: string;
  widgetActions?: WidgetAction[];
  onWidgetAction?: (action: WidgetAction, params: Record<string, string>) => void;
}

export function ResearchPanel({
  completed = [],
  inProgress = [],
  queue = [],
  researchPoints = 0,
  pointsPerTurn = 0,
  currency = "RP",
  widgetActions,
  onWidgetAction,
}: ResearchPanelProps) {
  const { t } = useLocale();
  return (
    <div className="flex flex-col gap-3 p-3 text-xs">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-primary" />
          <span className="font-semibold text-sm">{t.play_research_tree}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge variant="secondary" className="text-[10px] gap-1">
            <FlaskConical className="w-3 h-3" />
            {researchPoints} {currency}
          </Badge>
          {pointsPerTurn > 0 && (
            <Badge variant="outline" className="text-[10px]">
              +{pointsPerTurn}/turn
            </Badge>
          )}
        </div>
      </div>

      {/* In Progress */}
      {inProgress.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> {t.play_research_in_progress}
          </p>
          {inProgress.map((r) => {
            const total = r.turns_required || 1;
            const remaining = r.turns_remaining || 0;
            const progress = ((total - remaining) / total) * 100;
            return (
              <div key={r.id} className="rounded-lg border border-border/40 bg-muted/10 p-2.5 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{r.name}</span>
                  <Badge variant="outline" className="text-[9px]">
                    {t.play_research_tier} {r.tier || "?"}
                  </Badge>
                </div>
                <Progress value={progress} className="h-1.5" />
                <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {remaining} {t.play_research_turn_left}
                  </span>
                  {r.category && <Badge variant="secondary" className="text-[9px] h-4">{r.category}</Badge>}
                </div>
                {r.effects && <p className="text-[10px] text-muted-foreground/70 italic">→ {r.effects}</p>}
              </div>
            );
          })}
        </div>
      )}

      {/* Queue */}
      {queue.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1">
            <Clock className="w-3 h-3" /> {t.play_research_queued}
          </p>
          {queue.map((r, i) => (
            <div key={r.id || i} className="flex items-center gap-2 px-2 py-1 rounded bg-muted/5 border border-dashed border-border/30">
              <span className="text-muted-foreground/50 text-[9px]">#{i + 1}</span>
              <span>{r.name}</span>
            </div>
          ))}
        </div>
      )}

      {/* Completed */}
      {completed.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3 text-green-500" /> {t.play_research_completed} ({completed.length})
          </p>
          <div className="space-y-1">
            {completed.map((r) => (
              <div key={r.id} className="flex items-center gap-2 px-2 py-1 rounded bg-primary/5 border border-primary/10">
                <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" />
                <span className="flex-1">{r.name}</span>
                <Badge variant="outline" className="text-[9px] h-4">T{r.tier || "?"}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {completed.length === 0 && inProgress.length === 0 && queue.length === 0 && (
        <div className="text-center py-6 text-muted-foreground/50">
          <GitBranch className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-[11px]">{t.play_research_none}</p>
          <p className="text-[10px] mt-1">{t.play_research_ask}</p>
        </div>
      )}

      {/* Actions */}
      {widgetActions && widgetActions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1 border-t border-border/20">
          {widgetActions.map((action) => (
            <Button
              key={action.id}
              variant="outline"
              size="sm"
              className="h-6 text-[10px] px-2"
              onClick={() => onWidgetAction?.(action, {})}
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Compact status bar widget */
export function ResearchStatusWidget({
  completedCount = 0,
  inProgressCount = 0,
  researchPoints = 0,
  currency = "RP",
}: {
  completedCount?: number;
  inProgressCount?: number;
  researchPoints?: number;
  currency?: string;
}) {
  if (completedCount === 0 && inProgressCount === 0 && researchPoints === 0) return null;
  return (
    <div className="flex items-center gap-1.5 text-[10px]">
      <FlaskConical className="w-3 h-3 text-primary" />
      <span>{researchPoints} {currency}</span>
      {inProgressCount > 0 && (
        <Badge variant="secondary" className="text-[9px] h-3.5 px-1">
          {inProgressCount} active
        </Badge>
      )}
      {completedCount > 0 && (
        <span className="text-muted-foreground">✓{completedCount}</span>
      )}
    </div>
  );
}
